import React, { ReactElement } from 'react'
import { Box, Chip, Typography } from '@mui/material'

import { AISearchIcon } from '~assets/icons'
import { QueryAnalysis, QueryTypeEnum } from '~types'
import { toTitleCase } from '~utils/textUtils'

interface QueryAnalysisDebugInfoProps {
  queryAnalysis: QueryAnalysis
}

export const QueryAnalysisDebugInfo = ({
  queryAnalysis,
}: QueryAnalysisDebugInfoProps): ReactElement => {
  const QueryTypeChip = ({ queryType }: { queryType: QueryTypeEnum }): ReactElement => {
    const getChipColor = (type: QueryTypeEnum): 'primary' | 'secondary' | 'warning' | 'error' => {
      switch (type) {
        case 'KEYWORD':
        case 'QUESTION':
          return 'primary'
        case 'CONVERSATION':
          return 'warning'
        case 'EMERGENCY':
          return 'error'
        default:
          return 'primary'
      }
    }
    const color = getChipColor(queryType)
    return (
      <>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          Type:
        </Typography>
        <Chip size='small' label={toTitleCase(queryType)} color={color} sx={{ ml: 1 }} />
      </>
    )
  }

  const ContextDependentChip = ({
    isContextDependent,
  }: {
    isContextDependent: boolean
  }): ReactElement => {
    return (
      <>
        <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
          Context Dependent:
        </Typography>
        <Chip
          size='small'
          label={isContextDependent ? 'Yes' : 'No'}
          color={'default'}
          sx={{ ml: 1 }}
        />
      </>
    )
  }

  const LinkedSourcesChip = ({ useLinkedSources }: { useLinkedSources: boolean }): ReactElement => {
    return (
      <>
        <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
          Linked Sources:
        </Typography>
        <Chip
          size='small'
          label={useLinkedSources ? 'Yes' : 'No'}
          color={'default'}
          sx={{ ml: 1 }}
        />
      </>
    )
  }

  return (
    <Box padding={1} sx={{ display: 'flex' }} tabIndex={-1}>
      <Box sx={{ className: 'flex-v-align', alignItems: 'flex-start', mr: 2 }}>
        <AISearchIcon height={15} width={15} />
        <Typography
          variant='caption'
          sx={{ ml: 1, color: 'text.secondary', fontWeight: 'bold', fontSize: 14 }}
        >
          Query Analysis:
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box className='flex-v-align' justifyContent={'start'}>
          <QueryTypeChip queryType={queryAnalysis.query_type} />
          <ContextDependentChip isContextDependent={queryAnalysis.is_context_dependent} />
          <LinkedSourcesChip
            useLinkedSources={queryAnalysis.target_organization_references?.length !== 0}
          />
        </Box>

        <Box maxWidth={400} sx={{ mt: 1 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Rewrite:
          </Typography>
          <Typography
            variant='caption'
            sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}
          >
            {queryAnalysis.rewritten_query}
          </Typography>
        </Box>
        {queryAnalysis.primary_keyword && (
          <Box maxWidth={400} sx={{ mt: 1 }}>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Keywords:
            </Typography>
            <Typography
              variant='caption'
              sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}
            >
              {queryAnalysis.primary_keyword}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
